import { Link, useParams } from 'react-router-dom';
import Seo from '@/components/feature/Seo';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import ArticleCard from '@/components/feature/ArticleCard';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import { fmtNum, tagClass, canonicalUrl } from '@/utils/helpers';

export default function PostPage() {
  const { id = '' } = useParams();
  const article = articles.find((a) => a.id === Number(id));
  if (!article) {
    return (
      <>
        <SiteHeader />
        <main className="mx-auto flex w-full max-w-6xl flex-col items-center px-4 py-24 text-center">
          <i className="ri-question-mark text-5xl text-background-300" aria-hidden="true" />
          <h1 className="mt-4 text-2xl font-bold">这条瓜讯不存在或已被下架</h1>
          <p className="mt-2 text-sm text-foreground-600">看看首页最新瓜田，别错过下一颗大瓜。</p>
          <Link to="/" className="mt-6 inline-flex items-center whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600">
            <i className="ri-arrow-left-line mr-1.5" aria-hidden="true" /> 返回首页
          </Link>
        </main>
        <SiteFooter />
      </>
    );
  }

  const cat = categories.find((c) => c.slug === article.cat);
  const related = articles.filter((a) => a.cat === article.cat && a.id !== article.id).slice(0, 3);
  const schema = [
    {
      '@context': 'https://schema.org',
      '@type': article.timeline ? 'NewsArticle' : 'BlogPosting',
      headline: article.title,
      description: article.summary,
      image: [article.cover],
      datePublished: article.date,
      author: { '@type': 'Organization', name: '每日大瓜' },
      publisher: { '@type': 'Organization', name: '每日大瓜' },
      inLanguage: 'zh-CN',
      mainEntityOfPage: canonicalUrl(),
      articleSection: cat?.name,
      keywords: article.tag,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: '/' },
        { '@type': 'ListItem', position: 2, name: cat?.name, item: `/category/${cat?.slug}` },
        { '@type': 'ListItem', position: 3, name: article.title, item: canonicalUrl() },
      ],
    },
  ];

  return (
    <>
      <Seo
        title={`${article.title} | 每日大瓜`}
        description={article.summary}
        keywords={`${article.tag},每日大瓜,全网吃瓜时间线,${article.summary.slice(0, 12)}`}
        image={article.cover}
        type="article"
        jsonLd={schema}
      />
      <SiteHeader />

      <main>
        <div className="mx-auto w-full max-w-3xl px-4 md:px-6 py-6">
          <nav aria-label="面包屑" className="mb-4 flex flex-wrap items-center gap-1.5 text-sm text-foreground-500">
            <Link to="/" className="inline-flex items-center gap-1 hover:text-primary-700">
              <i className="ri-home-5-line" aria-hidden="true" /> 首页
            </Link>
            <i className="ri-arrow-right-s-line" aria-hidden="true" />
            <Link to={`/category/${cat?.slug}`} className="hover:text-primary-700">{cat?.name}</Link>
            <i className="ri-arrow-right-s-line" aria-hidden="true" />
            <span className="line-clamp-1 max-w-[200px] text-foreground-800">{article.title}</span>
          </nav>

          <div className="flex flex-wrap items-center gap-2">
            <span className={`inline-flex items-center rounded-md border px-2.5 py-1 text-sm font-medium ${tagClass(article.tag)}`}>
              <i className="ri-price-tag-3-line mr-1" aria-hidden="true" /> {article.tag}
            </span>
            {article.hotReversal && (
              <span className="inline-flex items-center gap-1 rounded-md bg-primary-500 px-2.5 py-1 text-sm font-semibold text-background-50">
                <i className="ri-flashlight-fill" aria-hidden="true" /> 瓜中瓜连环反转
              </span>
            )}
          </div>

          <h1 className="mt-3 text-2xl md:text-3xl font-bold leading-snug">{article.title}</h1>
          <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">{article.summary}</p>

          <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 border-y border-background-200 py-3 text-sm text-foreground-500">
            <span className="inline-flex items-center gap-1.5">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-100 text-primary-700">
                <i className="ri-user-3-line" aria-hidden="true" />
              </span>
              <span className="font-medium text-foreground-700">{article.author} · 每日大瓜</span>
            </span>
            <span className="inline-flex items-center gap-1 whitespace-nowrap"><i className="ri-calendar-line" aria-hidden="true" />{article.date}</span>
            <span className="inline-flex items-center gap-1 whitespace-nowrap"><i className="ri-eye-line" aria-hidden="true" />{fmtNum(article.reads)}热度</span>
            <span className="inline-flex items-center gap-1 whitespace-nowrap"><i className="ri-chat-3-line" aria-hidden="true" />{fmtNum(article.comments)}讨论</span>
            <span className="hidden items-center gap-1 whitespace-nowrap sm:inline-flex"><i className="ri-time-line" aria-hidden="true" />阅读约 {article.duration}</span>
          </div>
        </div>

        <div className="mx-auto w-full max-w-3xl px-4 md:px-6 pb-10">
          <div className="overflow-hidden rounded-lg border border-background-200">
            <img src={article.cover} alt={article.alt} title={`${article.title} - 每日大瓜`} className="w-full object-cover" />
          </div>

          <div className="mt-8 space-y-5">
            {article.paragraphs.map((p, i) => (
              <p key={i} className="text-[15px] leading-[1.9] text-foreground-800">{p}</p>
            ))}
          </div>

          {article.timeline && article.timeline.length > 0 && (
            <section className="mt-10 rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
              <h2 className="flex items-center gap-2 text-xl font-bold">
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-accent-500 text-background-50"><i className="ri-time-line" /></span>
                事件时间线
              </h2>
              <p className="mt-1 text-sm text-foreground-600">按时间顺序还原这场大戏的完整脉络</p>
              <ol className="mt-6 space-y-0">
                {article.timeline.map((ev, i) => (
                  <li key={i} className="relative flex gap-4 pb-6 last:pb-0">
                    <div className="flex flex-col items-center">
                      <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-bold ${
                        ev.hot ? 'bg-primary-500 text-background-50' : 'bg-accent-100 text-accent-800'
                      }`}>
                        {i + 1}
                      </span>
                      {i < article.timeline!.length - 1 && <span className="mt-1 w-px flex-1 bg-background-300" />}
                    </div>
                    <div className="min-w-0 pt-0.5">
                      <p className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
                        <span className="inline-flex items-center gap-1 font-medium text-accent-700">
                          <i className="ri-time-line" aria-hidden="true" /> {ev.time}
                        </span>
                        {ev.hot && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-primary-100 px-2 py-0.5 font-medium text-primary-700">
                            <i className="ri-fire-fill" aria-hidden="true" /> 关键节点
                          </span>
                        )}
                      </p>
                      <h3 className="mt-1 text-base font-bold text-foreground-950">{ev.title}</h3>
                      <p className="mt-1 text-sm leading-relaxed text-foreground-600">{ev.body}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </section>
          )}

          {article.evidence && article.evidence.length > 0 && (
            <section className="mt-6 rounded-lg border border-primary-200/70 bg-primary-50/50 p-5 md:p-6">
              <h2 className="flex items-center gap-2 text-lg font-bold">
                <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-500 text-background-50"><i className="ri-list-check-3" /></span>
                关键证据梳理
              </h2>
              <ul className="mt-4 grid gap-2.5 sm:grid-cols-2">
                {article.evidence.map((e) => (
                  <li key={e} className="flex items-start gap-2 rounded-md bg-background-50 border border-background-200 px-3 py-2.5 text-sm text-foreground-700">
                    <i className="ri-checkbox-circle-line mt-0.5 text-primary-600" aria-hidden="true" />
                    {e}
                  </li>
                ))}
              </ul>
              <p className="mt-3 text-xs text-foreground-500">说明：以上仅为公开讨论素材整理，非权威结论，请理性看待。</p>
            </section>
          )}

          <div className="mt-8 flex items-center justify-between border-t border-background-200 pt-5">
            <div className="flex items-center gap-2 text-sm text-foreground-600">
              <i className={`${cat?.icon} text-primary-600`} aria-hidden="true" />
              栏目：<Link to={`/category/${cat?.slug}`} className="font-medium text-primary-600 hover:underline">{cat?.name}</Link>
            </div>
            <span className="rounded-full bg-accent-100 px-3 py-1 text-xs text-accent-800"># 持续追踪中</span>
          </div>
        </div>

        {related.length > 0 && (
          <section className="mx-auto w-full max-w-6xl px-4 md:px-6 pb-12">
            <div className="mb-4 flex items-center gap-2">
              <span className="flex h-8 w-8 items-center justify-center rounded-md bg-secondary-100 text-secondary-800"><i className="ri-file-list-3-line" /></span>
              <h2 className="text-xl font-bold">相关推荐 · 同栏目瓜讯</h2>
            </div>
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((a) => (
                <ArticleCard key={a.id} article={a} />
              ))}
            </div>
          </section>
        )}
      </main>

      <SiteFooter />
    </>
  );
}